import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import type { DeepResearchFailure } from "@/pages/Private/pages/ProfileTracker/providers/deepResearchRunState";

interface ResearchFailureNoticeProps {
	failure: DeepResearchFailure;
	/** Runs the research again; not offered for a refusal to spend, which a retry cannot fix. */
	onRetry: () => void;
	onDismiss: () => void;
	isRetrying?: boolean;
}

/**
 * The lead's last research failed (PREC-4052). Shown above an older result as well as on its own,
 * so a failed rerun is not hidden behind the research it did not replace. A timeout or any other
 * failure offers "Try again" (a research is charged only when it succeeds); "Insufficient credits"
 * says where to add credits instead.
 */
export const ResearchFailureNotice: FunctionComponent<ResearchFailureNoticeProps> = ({
	failure,
	onRetry,
	onDismiss,
	isRetrying = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`profileTracker.deepResearch.${key}`);
	const isCredits = failure.kind === "credits";

	return (
		<div
			className="w-full rounded-lg border border-status-error/30 bg-status-error-light p-3"
			data-testid="research-failure"
			role="alert"
		>
			<div className="flex items-start justify-between gap-2">
				<p className="text-sm text-status-error">{ts(isCredits ? "failedCredits" : "failed")}</p>
				<button
					aria-label={ts("dismiss")}
					className="flex-shrink-0 cursor-pointer text-xs text-status-error/70 hover:text-status-error"
					type="button"
					onClick={onDismiss}
				>
					✕
				</button>
			</div>
			{!isCredits && (
				<button
					className="mt-2 cursor-pointer text-sm font-semibold text-blue-action underline disabled:cursor-not-allowed disabled:opacity-60"
					disabled={isRetrying}
					type="button"
					onClick={onRetry}
				>
					{ts("tryAgain")}
				</button>
			)}
		</div>
	);
};
